import { useEffect, useRef } from "react";
import { toast } from "react-toastify";

import type { AgenticLogicRulePublishJobDto } from "@intelligems/ig-types/dtos/agentic-logic/publish";
import type { AgenticLogicRuleVersionDto } from "@intelligems/ig-types/dtos/agentic-logic/versions";

import { experiencesLabel } from "./ruleSaveApplyState";

/**
 * The toast for a publish this visit watched run: "Applied · vN is live in N
 * experiences" on a rule experiences use, and a plain confirmation on one
 * nothing uses yet (its first publish is automatic, and it serves no one).
 *
 * Only runs seen queued or running here are announced, once each, and only
 * after the versions list shows the version they produced — a job hydrated
 * from history is state, not news, and must never replay on a reload.
 */
export const usePublishAnnouncement = ({
  publishJob,
  activeVersion,
  usedInCount,
}: {
  publishJob:
    | Pick<
        AgenticLogicRulePublishJobDto,
        "id" | "status" | "mode" | "resultingVersionId"
      >
    | null
    | undefined;
  activeVersion: Pick<AgenticLogicRuleVersionDto, "id" | "version"> | null;
  usedInCount: number | null;
}) => {
  const watched = useRef(new Set<string>());
  const announced = useRef(new Set<string>());

  useEffect(() => {
    if (!publishJob || publishJob.mode !== "publish") {
      return;
    }
    if (publishJob.status === "queued" || publishJob.status === "running") {
      watched.current.add(publishJob.id);
      return;
    }
    if (
      publishJob.status !== "succeeded" ||
      !watched.current.has(publishJob.id) ||
      announced.current.has(publishJob.id) ||
      activeVersion?.id !== publishJob.resultingVersionId
    ) {
      return;
    }
    announced.current.add(publishJob.id);
    toast.success(
      usedInCount
        ? `Applied · v${activeVersion.version} is live in ${experiencesLabel(usedInCount)}`
        : "Rule published successfully."
    );
  }, [publishJob, activeVersion, usedInCount]);
};
